import { supabase } from '@/lib/supabase';
import { fetchUserReseauIds, fetchNetworkMemberIds, fetchUserReseaux, type Reseau } from '@/lib/networks';
import type { OpeningHours } from '@/lib/restaurantHours';

// ─────────────────────────────────────────────────────────────
// ZIFEK — Données du site public « Marketplace mixte »
// Le marchand propriétaire de la marketplace (type de compte
// « Marketplace mixte produits et services ») agrège les contenus
// publiés par ses vendeurs :
//   • annonces  : le reste (immobilier, véhicules, emploi…)
//   • produits  : annonces de type « produits »
//   • services  : annonces de type « services »
// Les vendeurs sont rattachés à la marketplace via `userwebsite`.
// ─────────────────────────────────────────────────────────────

export const ANNONCE_TYPE_PRODUITS = 'produits';
export const ANNONCE_TYPE_SERVICES = 'services';

export type AnnonceKind = 'produits' | 'services' | 'autres' | 'all';

export interface MarketplaceAnnonce {
  id: number;
  titre: string;
  type: string | null;
  description: string | null;
  prix: string | null;
  ville: string | null;
  pays: string | null;
  telephone: string | null;
  image: string | null;
  /** Vidéos additionnelles, séparées par « | ». */
  video: string | null;
  idcategorie: number | null;
  /** Catégorie mère (renseignée quand le contenu est rangé dans une sous-catégorie). */
  idcategorie_parent: number | null;
  /** Catégorie grand-mère (renseignée quand le contenu est rangé dans une sous-sous-catégorie). */
  idcategorie_grandparent: number | null;
  idvendeur: number | null;
  owner: string | null;
  /** Marqueur des annonces publiées par un visiteur sans compte (« public »). */
  sp?: string | null;
  is_boost: boolean;
  /** Annonce mise « à la une » par le vendeur, visible uniquement sur sa boutique. */
  is_vendor_featured: boolean;
  /** Disponibilité déclarée par le vendeur (false = rupture de stock / non disponible). */
  disponible: boolean | null;
  publique: number;
  created: string | null;
}

/**
 * Vrai si un contenu est marqué en rupture de stock / non disponible par son
 * vendeur. Par défaut (champ absent ou null) le contenu reste disponible.
 */
export function isAnnonceUnavailable(
  annonce: { disponible?: boolean | null } | null | undefined,
): boolean {
  return annonce?.disponible === false;
}

export interface MarketplaceSeller {
  userId: number;
  nomcommerce: string;
  name: string;
  description: string;
  telephone: string;
  ville: string;
  pays: string;
  image: string;
  /** Horaires d'ouverture (texte libre historique). */
  horaires?: string;
  /** Horaires d'ouverture structurés par jour. */
  horairesJson?: OpeningHours | null;
  /** Réseaux sociaux de la boutique. */
  lienfacebook?: string;
  lieninstagram?: string;
  lientiktok?: string;
  lientwitter?: string;
  lienlinkedin?: string;
  lienyoutube?: string;
  /** Vendeur mis en avant par la marketplace. */
  isFeatured?: boolean;
  /** Nombre d'annonces publiées (renseigné à la demande). */
  annonceCount?: number;
}

export interface MarketplaceCategory {
  id: number;
  nom: string;
  icon: string | null;
  image_couverture: string | null;
  parent_id: number | null;
  sort_order: number;
  type: string | null;
}

export interface MarketplaceAdBanner {
  id: number;
  titre: string;
  description: string;
  image: string;
  click: number;
}

/** État de validation d'un vendeur par le propriétaire de la marketplace. */
export type VendorValidation = 'en_attente' | 'approuve' | 'refuse';

/** Relation vendeur ↔ marketplace (table userwebsite). */
export interface VendorRelation {
  relationId: number;
  validation: VendorValidation;
  statut: number | null;
  /** Catégorie principale déclarée par le vendeur. */
  categorieId: number | null;
  categorieNom: string | null;
  /** Sous-catégorie déclarée (si la catégorie en possède). */
  souscategorieId: number | null;
  souscategorieNom: string | null;
  /** Sous-sous-catégorie déclarée (3e niveau, si la sous-catégorie en possède). */
  soussouscategorieId: number | null;
  soussouscategorieNom: string | null;
}

export const VENDOR_VALIDATIONS: VendorValidation[] = ['en_attente', 'approuve', 'refuse'];

/** Normalise la valeur stockée en colonne `validation`. */
export function normalizeValidation(value: unknown): VendorValidation {
  const v = String(value || '').trim();
  return (VENDOR_VALIDATIONS as string[]).includes(v) ? (v as VendorValidation) : 'en_attente';
}

export function isProductType(type?: string | null): boolean {
  return (type || '').trim().toLowerCase() === ANNONCE_TYPE_PRODUITS;
}

export function isServiceType(type?: string | null): boolean {
  return (type || '').trim().toLowerCase() === ANNONCE_TYPE_SERVICES;
}

/** Une annonce « classique » = ni produit, ni service. */
export function isClassifiedType(type?: string | null): boolean {
  return !isProductType(type) && !isServiceType(type);
}

/**
 * Chemin public de la fiche détaillée d'un contenu, adapté à sa nature :
 *   • produit  → /produit/:id
 *   • service  → /service/:id
 *   • annonce  → /annonce/:id
 */
export function contentDetailPath(annonce: { id: number; type?: string | null }): string {
  if (isProductType(annonce.type)) return `/produit/${annonce.id}`;
  if (isServiceType(annonce.type)) return `/service/${annonce.id}`;
  return `/annonce/${annonce.id}`;
}

// ── Vendeurs rattachés à la marketplace ────────────────────────

export async function fetchMarketplaceSellerIds(marketplaceId: number): Promise<number[]> {
  if (!marketplaceId) return [];
  try {
    const { data, error } = await supabase
      .from('userwebsite')
      .select('iduser, statut, validation')
      .eq('idcommerce', marketplaceId)
      .eq('validation', 'approuve');
    const own = (error || !data)
      ? []
      : (data as { iduser: number; statut: number | null }[])
          .filter((r) => r.statut == null || Number(r.statut) === 1)
          .map((r) => Number(r.iduser))
          .filter((n) => Number.isFinite(n) && n > 0);
    // Réseau Zifek Commerce : si cette marketplace a rejoint le réseau,
    // on agrège aussi les commerces du réseau qui ont choisi d'y apparaître.
    const network = await fetchNetworkSellerIds(marketplaceId);
    return [...new Set([...own, ...network])];
  } catch {
    return [];
  }
}

// ── Réseau Zifek Commerce ──────────────────────────────────────

/**
 * Commerces du réseau Zifek Commerce à afficher sur une marketplace.
 * Renvoie [] si la marketplace n'a pas rejoint le réseau. Un commerce
 * rejoint le réseau en activant `users.reseau_zifek` depuis son dashboard.
 */
export async function fetchNetworkSellerIds(marketplaceId: number): Promise<number[]> {
  if (!marketplaceId) return [];
  try {
    const result = new Set<number>();

    // 1) Réseaux rejoints par la marketplace, dont la cible l'inclut.
    const memberships = await fetchUserReseaux(marketplaceId);
    const targetIds = memberships
      .filter((r) => r.cible === 'both' || r.cible === 'marketplace')
      .map((r) => r.id);
    if (targetIds.length > 0) {
      const ids = await fetchNetworkMemberIds(targetIds, marketplaceId);
      ids.forEach((n) => result.add(n));
    }

    // 2) Compatibilité historique : drapeau `users.reseau_zifek`.
    const { data: market } = await supabase
      .from('users')
      .select('reseau_zifek')
      .eq('id', marketplaceId)
      .maybeSingle();
    if (Number((market as { reseau_zifek?: number } | null)?.reseau_zifek) === 1) {
      const { data } = await supabase
        .from('users')
        .select('id')
        .eq('reseau_zifek', 1)
        .neq('id', marketplaceId)
        .neq('type', 'Client');
      (data as { id: number }[] | null)?.forEach((r) => {
        const n = Number(r.id);
        if (Number.isFinite(n) && n > 0) result.add(n);
      });
    }

    return [...result];
  } catch {
    return [];
  }
}

/** Réseaux (titres) dont un vendeur / commerce est membre — pour les badges. */
export async function fetchSellerNetworks(userId: number): Promise<Reseau[]> {
  return fetchUserReseaux(userId);
}

/** Identifiants des réseaux d'un compte (lecture rapide). */
export async function fetchSellerReseauIds(userId: number): Promise<number[]> {
  return fetchUserReseauIds(userId);
}

/** État d'appartenance d'un compte au réseau Zifek Commerce. */
export async function fetchNetworkStatus(userId: number): Promise<boolean> {
  if (!userId) return false;
  try {
    const { data } = await supabase
      .from('users')
      .select('reseau_zifek')
      .eq('id', userId)
      .maybeSingle();
    return Number((data as { reseau_zifek?: number } | null)?.reseau_zifek) === 1;
  } catch {
    return false;
  }
}

/** Active / désactive l'appartenance au réseau Zifek Commerce. */
export async function setNetworkStatus(userId: number, on: boolean): Promise<void> {
  const { error } = await supabase
    .from('users')
    .update({ reseau_zifek: on ? 1 : 0 })
    .eq('id', userId);
  if (error) throw error;
}

export async function fetchSellersByIds(ids: number[]): Promise<MarketplaceSeller[]> {
  const unique = [...new Set(ids.filter((n) => Number.isFinite(n) && n > 0))];
  if (unique.length === 0) return [];
  try {
    const { data, error } = await supabase
      .from('users')
      .select('id, nomcommerce, name, description, telephone, Ville, Pays, image, horaires, horaires_json, lienfacebook, lieninstagram, lientiktok, lientwitter, lienlinkedin, lienyoutube')
      .in('id', unique);
    if (error || !data) return [];
    return (data as Record<string, unknown>[]).map((u) => ({
      userId: Number(u.id),
      nomcommerce: (u.nomcommerce as string) || (u.name as string) || 'Vendeur',
      name: (u.name as string) || '',
      description: (u.description as string) || '',
      telephone: (u.telephone as string) || '',
      ville: (u.Ville as string) || '',
      pays: (u.Pays as string) || '',
      image: (u.image as string) || '',
      horaires: (u.horaires as string) || '',
      horairesJson: (u.horaires_json as OpeningHours | null) || null,
      lienfacebook: (u.lienfacebook as string) || '',
      lieninstagram: (u.lieninstagram as string) || '',
      lientiktok: (u.lientiktok as string) || '',
      lientwitter: (u.lientwitter as string) || '',
      lienlinkedin: (u.lienlinkedin as string) || '',
      lienyoutube: (u.lienyoutube as string) || '',
    }));
  } catch {
    return [];
  }
}

export async function fetchMarketplaceSellers(marketplaceId: number): Promise<MarketplaceSeller[]> {
  const ids = await fetchMarketplaceSellerIds(marketplaceId);
  return fetchSellersByIds(ids);
}

// ── Catégories de la marketplace ───────────────────────────────

export async function fetchMarketplaceCategories(
  marketplaceId: number,
  opts?: { type?: string },
): Promise<MarketplaceCategory[]> {
  if (!marketplaceId) return [];
  try {
    // Isolation stricte : chaque marketplace n'affiche QUE ses propres
    // catégories (idcommerce = cette marketplace). Les catégories globales
    // (idcommerce = 0 / null) et celles des autres commerces sont exclues.
    const { data, error } = await supabase
      .from('categorie')
      .select('id, nom, icon, image_couverture, parent_id, sort_order, type, afficher, idcommerce')
      .eq('idcommerce', marketplaceId)
      .order('sort_order', { ascending: true })
      .order('nom', { ascending: true });
    if (error || !data) return [];
    let list = (data as Record<string, unknown>[]).filter(
      (c) => c.afficher == null || Number(c.afficher) === 1,
    );
    if (opts?.type) {
      list = list.filter((c) => ((c.type as string) || '').toLowerCase() === opts.type!.toLowerCase());
    }
    return list.map((c) => ({
      id: Number(c.id),
      nom: (c.nom as string) || '',
      icon: (c.icon as string) || null,
      image_couverture: (c.image_couverture as string) || null,
      parent_id: c.parent_id == null ? null : Number(c.parent_id),
      sort_order: Number(c.sort_order || 0),
      type: (c.type as string) || null,
    }));
  } catch {
    return [];
  }
}

// ── Annonces publiées par les vendeurs ─────────────────────────

export async function fetchMarketplaceAnnonces(
  marketplaceId: number,
  opts?: { kind?: AnnonceKind; limit?: number; sellerIds?: number[] },
): Promise<MarketplaceAnnonce[]> {
  const sellerIds = opts?.sellerIds ?? (await fetchMarketplaceSellerIds(marketplaceId));
  try {
    let list: MarketplaceAnnonce[] = [];
    if (sellerIds.length > 0) {
      const { data, error } = await supabase
        .from('annonce')
        .select('id, titre, type, description, prix, ville, pays, telephone, image, video, idcategorie, idcategorie_parent, idcategorie_grandparent, idvendeur, owner, sp, is_boost, disponible, created')
        .eq('publique', 1)
        .in('idvendeur', sellerIds)
        .order('created', { ascending: false });
      if (!error && data) list = data as unknown as MarketplaceAnnonce[];
    }

    // Annonces déposées par des visiteurs sans compte (1 photo, marqueur sp='public').
    const publicAds = await fetchPublicMarketplaceAnnonces(marketplaceId);
    const seen = new Set(list.map((a) => a.id));
    publicAds.forEach((a) => { if (!seen.has(a.id)) list.push(a); });

    const kind = opts?.kind ?? 'all';
    if (kind === 'produits') list = list.filter((a) => isProductType(a.type));
    else if (kind === 'services') list = list.filter((a) => isServiceType(a.type));
    else if (kind === 'autres') list = list.filter((a) => isClassifiedType(a.type));
    // Mise en avant : les annonces boostées remontent en tête (tri stable).
    list.sort((a, b) => Number(b.is_boost) - Number(a.is_boost));
    if (opts?.limit) list = list.slice(0, opts.limit);
    return list;
  } catch {
    return [];
  }
}

/** Villes distinctes présentes dans une liste d'annonces (filtre par ville). */
export function distinctCities(annonces: MarketplaceAnnonce[]): string[] {
  const set = new Set<string>();
  annonces.forEach((a) => {
    const v = (a.ville || '').trim();
    if (v) set.add(v);
  });
  return [...set].sort((a, b) => a.localeCompare(b, 'fr'));
}

// ── Bannières publicitaires (blocs « Recommandation ») ──────────

export async function fetchMarketplaceAdBanners(
  marketplaceId: number,
  limit = 2,
): Promise<MarketplaceAdBanner[]> {
  if (!marketplaceId) return [];
  try {
    const { data, error } = await supabase
      .from('bannierepublicitaire')
      .select('id, titre, descriptioncourte, imagecouverture, click')
      .eq('idcommerce', marketplaceId)
      .eq('idvendeur', 0)
      .order('numerobanniere', { ascending: true })
      .limit(limit);
    if (error || !data) return [];
    return (data as Record<string, unknown>[]).map((b) => ({
      id: Number(b.id),
      titre: (b.titre as string) || '',
      description: (b.descriptioncourte as string) || '',
      image: (b.imagecouverture as string) || '',
      click: Number(b.click || 0),
    }));
  } catch {
    return [];
  }
}

// ── Fiche détaillée d'une annonce ──────────────────────────────

const ANNONCE_COLUMNS =
  'id, titre, type, description, prix, ville, pays, telephone, image, video, idcategorie, idcategorie_parent, idcategorie_grandparent, idvendeur, owner, sp, is_boost, is_vendor_featured, disponible, publique, created';

/**
 * Annonces déposées par des visiteurs sans compte sur une marketplace.
 * Elles sont rattachées au commerce propriétaire et marquées `sp = 'public'`.
 */
export async function fetchPublicMarketplaceAnnonces(
  marketplaceId: number,
  limit?: number,
): Promise<MarketplaceAnnonce[]> {
  if (!marketplaceId) return [];
  try {
    let query = supabase
      .from('annonce')
      .select(ANNONCE_COLUMNS)
      .eq('publique', 1)
      .eq('sp', 'public')
      .eq('idvendeur', marketplaceId)
      .order('created', { ascending: false });
    if (limit) query = query.limit(limit);
    const { data, error } = await query;
    if (error || !data) return [];
    return (data as unknown as MarketplaceAnnonce[]) || [];
  } catch {
    return [];
  }
}

/**
 * Récupère une annonce publiée par son identifiant (fiche détaillée).
 *
 * Isolation par commerce : quand une marketplace est fournie, l'annonce
 * n'est renvoyée que si elle appartient à l'un des vendeurs rattachés à
 * cette marketplace (ou du réseau). Impossible donc d'afficher le contenu
 * d'un autre commerce sur la boutique d'un autre.
 */
export async function fetchMarketplaceAnnonceById(
  id: number,
  marketplaceId?: number | null,
): Promise<MarketplaceAnnonce | null> {
  if (!Number.isFinite(id) || id <= 0) return null;
  try {
    const { data, error } = await supabase
      .from('annonce')
      .select(ANNONCE_COLUMNS)
      .eq('id', id)
      .maybeSingle();
    if (error || !data) return null;
    const annonce = data as unknown as MarketplaceAnnonce;
    if (marketplaceId && marketplaceId > 0) {
      // Annonce publique déposée par un visiteur : rattachée à ce commerce.
      if (annonce.sp === 'public') {
        return Number(annonce.idvendeur) === Number(marketplaceId) ? annonce : null;
      }
      const sellerIds = await fetchMarketplaceSellerIds(marketplaceId);
      if (!annonce.idvendeur || !sellerIds.includes(Number(annonce.idvendeur))) {
        return null;
      }
    }
    return annonce;
  } catch {
    return null;
  }
}

/** Galerie photos additionnelle d'une annonce (table annonces_images). */
export async function fetchAnnonceGallery(annonceId: number): Promise<string[]> {
  if (!Number.isFinite(annonceId) || annonceId <= 0) return [];
  try {
    const { data, error } = await supabase
      .from('annonces_images')
      .select('id, image')
      .eq('idannonce', annonceId)
      .order('id', { ascending: true });
    if (error || !data) return [];
    return (data as { id: number; image: string | null }[])
      .map((r) => (r.image || '').trim())
      .filter(Boolean);
  } catch {
    return [];
  }
}

/** Vendeur (fiche users) propriétaire d'une annonce. */
export async function fetchAnnonceSeller(sellerId: number | null): Promise<MarketplaceSeller | null> {
  if (!sellerId) return null;
  const list = await fetchSellersByIds([sellerId]);
  return list[0] ?? null;
}

/** Service de réservation (table booking_services) d'un vendeur. */
export interface VendorBookingService {
  id: number;
  service_name: string;
  duration: number | null;
}

/**
 * Services de réservation actifs d'un vendeur.
 * Sert à afficher le bouton « Réserver » sur la boutique et les fiches service.
 */
export async function fetchVendorBookingServices(
  ownerId: number | null,
): Promise<VendorBookingService[]> {
  if (!ownerId || !Number.isFinite(ownerId)) return [];
  try {
    const { data, error } = await supabase
      .from('booking_services')
      .select('id, service_name, duration')
      .eq('owner', ownerId)
      .eq('statut', 1)
      .order('service_name', { ascending: true });
    if (error || !data) return [];
    return (data as Record<string, unknown>[]).map((r) => ({
      id: Number(r.id),
      service_name: (r.service_name as string) || 'Service',
      duration: r.duration == null ? null : Number(r.duration),
    }));
  } catch {
    return [];
  }
}

export interface VendorMarketplaceRef {
  id: number;
  name: string;
}

/**
 * Marketplace sur laquelle un vendeur est rattaché (là où il vend).
 * Utilisé pour mentionner la marketplace sur les fiches et cartes de contenu.
 */
export async function fetchVendorMarketplace(vendorId: number): Promise<VendorMarketplaceRef | null> {
  if (!vendorId || !Number.isFinite(vendorId)) return null;
  try {
    const { data: rels } = await supabase
      .from('userwebsite')
      .select('idcommerce, validation')
      .eq('iduser', vendorId)
      .eq('validation', 'approuve')
      .limit(1);
    const rel = (rels || [])[0] as { idcommerce: number } | undefined;
    if (!rel?.idcommerce) return null;
    const { data: m } = await supabase
      .from('users')
      .select('id, nomcommerce, name')
      .eq('id', rel.idcommerce)
      .maybeSingle();
    const row = m as { id: number; nomcommerce: string; name: string } | null;
    return {
      id: Number(rel.idcommerce),
      name: row ? (row.nomcommerce || row.name || '') : '',
    };
  } catch {
    return null;
  }
}

/**
 * Service de réservation par défaut choisi par un vendeur pour sa boutique.
 * Stocké dans `merchant_home_config.default_booking_service_id` (clé = id du vendeur).
 */
export async function fetchVendorDefaultBookingService(vendorId: number): Promise<number | null> {
  if (!vendorId || !Number.isFinite(vendorId)) return null;
  try {
    const { data } = await supabase
      .from('merchant_home_config')
      .select('default_booking_service_id')
      .eq('idcommerce', vendorId)
      .maybeSingle();
    const id = Number((data as { default_booking_service_id?: number | null } | null)?.default_booking_service_id);
    return Number.isFinite(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}

/** Définit (ou retire) le service de réservation par défaut d'un vendeur. */
export async function setVendorDefaultBookingService(
  vendorId: number,
  serviceId: number | null,
): Promise<void> {
  if (!vendorId) return;
  const value = serviceId && serviceId > 0 ? serviceId : null;
  const { data: existing } = await supabase
    .from('merchant_home_config')
    .select('idcommerce')
    .eq('idcommerce', vendorId)
    .maybeSingle();
  if (existing) {
    const { error } = await supabase
      .from('merchant_home_config')
      .update({ default_booking_service_id: value, updated_at: new Date().toISOString() })
      .eq('idcommerce', vendorId);
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('merchant_home_config')
      .insert({ idcommerce: vendorId, default_booking_service_id: value, blocks: [] });
    if (error) throw error;
  }
}

/**
 * Choisit le service de réservation à ouvrir pour une annonce « service » :
 * correspondance exacte par nom, sinon service par défaut, sinon premier service.
 */
export function resolveReserveServiceId(
  annonce: { titre?: string | null; type?: string | null },
  services: VendorBookingService[],
  defaultId: number | null,
): number | null {
  if (!isServiceType(annonce.type) || services.length === 0) return null;
  const title = (annonce.titre || '').trim().toLowerCase();
  const exact = services.find((s) => (s.service_name || '').trim().toLowerCase() === title);
  if (exact) return exact.id;
  if (defaultId && services.some((s) => s.id === defaultId)) return defaultId;
  return services[0].id;
}

/** Autres annonces publiées par le même vendeur (hors annonce courante). */
export async function fetchSellerOtherAnnonces(
  sellerId: number | null,
  excludeId: number,
  limit = 4,
): Promise<MarketplaceAnnonce[]> {
  if (!sellerId) return [];
  try {
    const { data, error } = await supabase
      .from('annonce')
      .select(ANNONCE_COLUMNS)
      .eq('publique', 1)
      .eq('idvendeur', sellerId)
      .order('created', { ascending: false })
      .limit(limit + 1);
    if (error || !data) return [];
    return (data as unknown as MarketplaceAnnonce[])
      .filter((a) => Number(a.id) !== Number(excludeId))
      .slice(0, limit);
  } catch {
    return [];
  }
}

// ── Vendeurs mis en avant ──────────────────────────────────────

/** Identifiants de relation `userwebsite` mis en avant par la marketplace. */
export async function fetchFeaturedSellerIds(marketplaceId: number): Promise<number[]> {
  if (!marketplaceId) return [];
  try {
    const { data, error } = await supabase
      .from('userwebsite')
      .select('iduser, mis_en_avant')
      .eq('idcommerce', marketplaceId)
      .eq('mis_en_avant', true);
    if (error || !data) return [];
    return (data as { iduser: number }[])
      .map((r) => Number(r.iduser))
      .filter((n) => Number.isFinite(n) && n > 0);
  } catch {
    return [];
  }
}

/** Vendeurs mis en avant (si aucun, renvoie une liste vide). */
export async function fetchFeaturedSellers(marketplaceId: number): Promise<MarketplaceSeller[]> {
  const ids = await fetchFeaturedSellerIds(marketplaceId);
  if (ids.length === 0) return [];
  const sellers = await fetchSellersByIds(ids);
  return sellers.map((s) => ({ ...s, isFeatured: true }));
}

/**
 * Résout les noms de catégories à partir de leurs identifiants (table
 * `categorie` partagée). Sert à reconstruire la chaîne complète
 * Catégorie › Sous-catégorie › Sous-sous-catégorie sur les fiches publiques.
 */
export async function fetchCategoryNames(
  ids: (number | null | undefined)[],
): Promise<Record<number, string>> {
  const clean = [
    ...new Set(
      ids
        .map((n) => Number(n))
        .filter((n) => Number.isFinite(n) && n > 0),
    ),
  ];
  if (clean.length === 0) return {};
  try {
    const { data, error } = await supabase
      .from('categorie')
      .select('id, nom')
      .in('id', clean);
    if (error || !data) return {};
    const map: Record<number, string> = {};
    (data as { id: number; nom: string | null }[]).forEach((c) => {
      if (c.nom) map[Number(c.id)] = c.nom;
    });
    return map;
  } catch {
    return {};
  }
}

// ── Gestion marketplace (dashboard) ────────────────────────────

/** Ligne de vendeur pour l'écran de gestion (relations + profil). */
export interface MarketplaceVendorRow {
  relationId: number;
  userId: number;
  nomcommerce: string;
  ville: string;
  pays: string;
  telephone: string;
  image: string;
  statut: number | null;
  validation: VendorValidation;
  misEnAvant: boolean;
  annonceCount: number;
  /** Date de la demande / du rattachement. */
  createdAt: string | null;
}

/**
 * Annonces de tous les vendeurs de la marketplace (écran de gestion),
 * triées par mise en avant puis par date.
 */
export async function fetchMarketplaceAdminAnnonces(
  marketplaceId: number,
): Promise<MarketplaceAnnonce[]> {
  const sellerIds = await fetchMarketplaceSellerIds(marketplaceId);
  if (sellerIds.length === 0) return [];
  try {
    const { data, error } = await supabase
      .from('annonce')
      .select(ANNONCE_COLUMNS)
      .in('idvendeur', sellerIds)
      .order('created', { ascending: false });
    if (error || !data) return [];
    const list = (data as unknown as MarketplaceAnnonce[]) || [];
    list.sort((a, b) => Number(b.is_boost) - Number(a.is_boost));
    return list;
  } catch {
    return [];
  }
}

/** Active / désactive la mise en avant d'une annonce. */
export async function setAnnonceBoost(annonceId: number, boost: boolean): Promise<void> {
  const { error } = await supabase.from('annonce').update({ is_boost: boost }).eq('id', annonceId);
  if (error) throw error;
}

/**
 * Met une annonce du vendeur « à la une » (une seule à la fois).
 * La mise en avant est locale à la boutique du vendeur (colonne dédiée
 * `is_vendor_featured`), indépendante du boost géré par la marketplace.
 */
export async function setVendorFeaturedAnnonce(vendorId: number, annonceId: number): Promise<void> {
  if (!vendorId || !annonceId) return;
  const { error: onErr } = await supabase
    .from('annonce')
    .update({ is_vendor_featured: true })
    .eq('id', annonceId)
    .eq('idvendeur', vendorId);
  if (onErr) throw onErr;
  await supabase
    .from('annonce')
    .update({ is_vendor_featured: false })
    .eq('idvendeur', vendorId)
    .neq('id', annonceId);
}

/** Retire toute mise « à la une » des annonces du vendeur. */
export async function clearVendorFeaturedAnnonce(vendorId: number): Promise<void> {
  if (!vendorId) return;
  const { error } = await supabase
    .from('annonce')
    .update({ is_vendor_featured: false })
    .eq('idvendeur', vendorId);
  if (error) throw error;
}

/** Vendeurs rattachés à la marketplace, avec leur état de mise en avant. */
export async function fetchMarketplaceVendors(
  marketplaceId: number,
): Promise<MarketplaceVendorRow[]> {
  if (!marketplaceId) return [];
  try {
    const { data: relations, error } = await supabase
      .from('userwebsite')
      .select('id, iduser, statut, mis_en_avant, validation, date')
      .eq('idcommerce', marketplaceId);
    if (error || !relations) return [];
    const rows = relations as { id: number; iduser: number; statut: number | null; mis_en_avant: boolean | null; validation: string | null; date: string | null }[];
    const userIds = rows.map((r) => Number(r.iduser)).filter((n) => Number.isFinite(n) && n > 0);
    const sellers = await fetchSellersByIds(userIds);
    const byId = new Map(sellers.map((s) => [s.userId, s]));

    // Comptage des annonces par vendeur.
    const counts = new Map<number, number>();
    if (userIds.length > 0) {
      const { data: annonces } = await supabase
        .from('annonce')
        .select('idvendeur')
        .in('idvendeur', userIds);
      (annonces as { idvendeur: number | null }[] | null)?.forEach((a) => {
        const key = Number(a.idvendeur);
        if (Number.isFinite(key)) counts.set(key, (counts.get(key) || 0) + 1);
      });
    }

    return rows
      .map((r) => {
        const s = byId.get(Number(r.iduser));
        return {
          relationId: Number(r.id),
          userId: Number(r.iduser),
          nomcommerce: s?.nomcommerce || 'Vendeur',
          ville: s?.ville || '',
          pays: s?.pays || '',
          telephone: s?.telephone || '',
          image: s?.image || '',
          statut: r.statut == null ? null : Number(r.statut),
          validation: normalizeValidation(r.validation),
          misEnAvant: Boolean(r.mis_en_avant),
          annonceCount: counts.get(Number(r.iduser)) || 0,
          createdAt: r.date || null,
        };
      })
      .sort((a, b) => Number(b.misEnAvant) - Number(a.misEnAvant) || b.annonceCount - a.annonceCount);
  } catch {
    return [];
  }
}

/** Active / désactive la mise en avant d'un vendeur (relation userwebsite). */
export async function setVendorFeatured(relationId: number, featured: boolean): Promise<void> {
  const { error } = await supabase
    .from('userwebsite')
    .update({ mis_en_avant: featured })
    .eq('id', relationId);
  if (error) throw error;
}

// ── Modération (propriétaire de la marketplace) ────────────────

/** Publie / dépublie une annonce d'un vendeur. */
export async function setAnnoncePubliee(annonceId: number, publique: boolean): Promise<void> {
  const { error } = await supabase.from('annonce').update({ publique: publique ? 1 : 0 }).eq('id', annonceId);
  if (error) throw error;
}

/**
 * Marque un contenu en rupture de stock / non disponible (ou de nouveau
 * disponible). Le vendeur pilote cette information depuis son tableau de bord.
 */
export async function setAnnonceDisponible(annonceId: number, disponible: boolean): Promise<void> {
  const { error } = await supabase.from('annonce').update({ disponible }).eq('id', annonceId);
  if (error) throw error;
}

/** Masque (0) ou réactive (1) un vendeur de la marketplace. */
export async function setVendorStatus(relationId: number, statut: number): Promise<void> {
  const { error } = await supabase.from('userwebsite').update({ statut }).eq('id', relationId);
  if (error) throw error;
}

/** Notifie un vendeur (cloche du dashboard / espace vendeur). */
export async function notifyVendor(
  userId: number,
  message: string,
  opts?: { senderId?: number; publicationId?: number; type?: string },
): Promise<void> {
  try {
    await supabase.from('notifications').insert({
      user_id: userId,
      sender_id: opts?.senderId ?? userId,
      publication_id: opts?.publicationId ?? 0,
      type: opts?.type ?? 'system',
      message,
      is_read: 0,
    });
  } catch {
    /* la notification est best-effort */
  }
}

// ── Validation des demandes de vente (propriétaire) ────────────

/**
 * Approuve ou refuse une demande de vente.
 * Quand la demande est approuvée, le vendeur est également réactivé (statut = 1).
 */
export async function setVendorValidation(
  relationId: number,
  validation: VendorValidation,
): Promise<void> {
  const patch: Record<string, unknown> = { validation };
  if (validation === 'approuve') patch.statut = 1;
  const { error } = await supabase.from('userwebsite').update(patch).eq('id', relationId);
  if (error) throw error;
}

/** Relation vendeur ↔ marketplace d'un utilisateur (pour l'espace vendeur). */
export async function fetchVendorRelation(
  marketplaceId: number,
  userId: number,
): Promise<VendorRelation | null> {
  if (!marketplaceId || !userId) return null;
  try {
    const { data, error } = await supabase
      .from('userwebsite')
      .select('id, validation, statut, categorie_id, categorie_nom, souscategorie_id, souscategorie_nom, soussouscategorie_id, soussouscategorie_nom')
      .eq('idcommerce', marketplaceId)
      .eq('iduser', userId)
      .maybeSingle();
    if (error || !data) return null;
    const row = data as {
      id: number;
      validation: string | null;
      statut: number | null;
      categorie_id: number | null;
      categorie_nom: string | null;
      souscategorie_id: number | null;
      souscategorie_nom: string | null;
      soussouscategorie_id: number | null;
      soussouscategorie_nom: string | null;
    };
    return {
      relationId: Number(row.id),
      validation: normalizeValidation(row.validation),
      statut: row.statut == null ? null : Number(row.statut),
      categorieId: row.categorie_id == null ? null : Number(row.categorie_id),
      categorieNom: row.categorie_nom || null,
      souscategorieId: row.souscategorie_id == null ? null : Number(row.souscategorie_id),
      souscategorieNom: row.souscategorie_nom || null,
      soussouscategorieId: row.soussouscategorie_id == null ? null : Number(row.soussouscategorie_id),
      soussouscategorieNom: row.soussouscategorie_nom || null,
    };
  } catch {
    return null;
  }
}

/**
 * Change la catégorie principale déclarée par un vendeur
 * (relation userwebsite). Le vendeur garde la liberté de classer chaque
 * produit/annonce dans d'autres catégories individuellement.
 */
export async function updateVendorCategory(
  relationId: number,
  categorieId: number | null,
  categorieNom: string | null,
  souscategorieId: number | null = null,
  souscategorieNom: string | null = null,
  soussouscategorieId: number | null = null,
  soussouscategorieNom: string | null = null,
): Promise<void> {
  const { error } = await supabase
    .from('userwebsite')
    .update({
      categorie_id: categorieId,
      categorie_nom: categorieNom,
      souscategorie_id: souscategorieId,
      souscategorie_nom: souscategorieNom,
      soussouscategorie_id: soussouscategorieId,
      soussouscategorie_nom: soussouscategorieNom,
    })
    .eq('id', relationId);
  if (error) throw error;
}

/** Catégorie principale déclarée d'un vendeur (depuis userwebsite). */
export async function fetchVendorDeclaredCategory(
  marketplaceId: number,
  userId: number,
): Promise<{ id: number | null; nom: string | null }> {
  const rel = await fetchVendorRelation(marketplaceId, userId);
  return { id: rel?.categorieId ?? null, nom: rel?.categorieNom ?? null };
}

export interface VendorCategoryLabels {
  categorieId: number | null;
  categorieNom: string | null;
  souscategorieId: number | null;
  souscategorieNom: string | null;
  soussouscategorieId: number | null;
  soussouscategorieNom: string | null;
}

/**
 * Catégories (et sous-catégories) déclarées de plusieurs vendeurs d'une
 * marketplace, en une seule requête. Sert à afficher la sous-catégorie du
 * vendeur sur sa boutique publique et sur les cartes vendeur des listes.
 */
export async function fetchVendorCategoryLabels(
  marketplaceId: number,
  userIds: number[],
): Promise<Record<number, VendorCategoryLabels>> {
  const ids = [...new Set(userIds.filter((n) => Number.isFinite(n) && n > 0))];
  if (!marketplaceId || ids.length === 0) return {};
  try {
    const { data, error } = await supabase
      .from('userwebsite')
      .select('iduser, categorie_id, categorie_nom, souscategorie_id, souscategorie_nom, soussouscategorie_id, soussouscategorie_nom')
      .eq('idcommerce', marketplaceId)
      .in('iduser', ids);
    if (error || !data) return {};
    const map: Record<number, VendorCategoryLabels> = {};
    (data as {
      iduser: number;
      categorie_id: number | null;
      categorie_nom: string | null;
      souscategorie_id: number | null;
      souscategorie_nom: string | null;
      soussouscategorie_id: number | null;
      soussouscategorie_nom: string | null;
    }[]).forEach((r) => {
      const key = Number(r.iduser);
      if (!Number.isFinite(key) || key <= 0) return;
      map[key] = {
        categorieId: r.categorie_id == null ? null : Number(r.categorie_id),
        categorieNom: r.categorie_nom || null,
        souscategorieId: r.souscategorie_id == null ? null : Number(r.souscategorie_id),
        souscategorieNom: r.souscategorie_nom || null,
        soussouscategorieId: r.soussouscategorie_id == null ? null : Number(r.soussouscategorie_id),
        soussouscategorieNom: r.soussouscategorie_nom || null,
      };
    });
    return map;
  } catch {
    return {};
  }
}

export interface VendorCategoryRef {
  id: number;
  nom: string;
}

/**
 * Catégories issues des annonces publiées d'un vendeur.
 * Un vendeur peut proposer des produits/services dans plusieurs catégories :
 * il apparaît donc dans chacune d'elles sur la marketplace.
 */
export async function fetchVendorProductCategories(vendorId: number): Promise<VendorCategoryRef[]> {
  if (!vendorId) return [];
  try {
    const { data: annonces, error } = await supabase
      .from('annonce')
      .select('idcategorie')
      .eq('idvendeur', vendorId)
      .eq('publique', 1);
    if (error || !annonces) return [];
    const ids = [
      ...new Set(
        (annonces as { idcategorie: number | null }[])
          .map((a) => Number(a.idcategorie))
          .filter((n) => Number.isFinite(n) && n > 0),
      ),
    ];
    if (ids.length === 0) return [];
    const { data: cats } = await supabase
      .from('categorie')
      .select('id, nom, sort_order')
      .in('id', ids)
      .order('sort_order', { ascending: true });
    return ((cats as { id: number; nom: string }[] | null) || [])
      .filter((c) => c.nom)
      .map((c) => ({ id: Number(c.id), nom: c.nom }));
  } catch {
    return [];
  }
}

// ── Annonces publiées d'un vendeur (page boutique du vendeur) ──

export async function fetchVendorPublishedAnnonces(vendorId: number): Promise<MarketplaceAnnonce[]> {
  if (!vendorId) return [];
  try {
    const { data, error } = await supabase
      .from('annonce')
      .select(ANNONCE_COLUMNS)
      .eq('publique', 1)
      .eq('idvendeur', vendorId)
      .order('created', { ascending: false });
    if (error || !data) return [];
    const list = (data as unknown as MarketplaceAnnonce[]) || [];
    // « À la une » du vendeur d'abord, puis boost marketplace, puis date.
    list.sort(
      (a, b) =>
        Number(b.is_vendor_featured) - Number(a.is_vendor_featured) ||
        Number(b.is_boost) - Number(a.is_boost),
    );
    return list;
  } catch {
    return [];
  }
}

// ── Bannières publicitaires d'un vendeur (page boutique) ───────

export interface VendorBanner {
  id: number;
  titre: string;
  description: string;
  image: string;
  /** Annonce à laquelle la bannière est rattachée (0 = aucune). */
  idannonce: number;
  click: number;
}

const VENDOR_BANNER_COLUMNS = 'id, titre, descriptioncourte, imagecouverture, idannonce, click';

function mapVendorBanner(row: Record<string, unknown>): VendorBanner {
  return {
    id: Number(row.id),
    titre: (row.titre as string) || '',
    description: (row.descriptioncourte as string) || '',
    image: (row.imagecouverture as string) || '',
    idannonce: Number(row.idannonce || 0),
    click: Number(row.click || 0),
  };
}

/** Bannières créées par un vendeur (visibles sur sa boutique). */
export async function fetchVendorBanners(
  marketplaceId: number,
  vendorId: number,
): Promise<VendorBanner[]> {
  if (!marketplaceId || !vendorId) return [];
  try {
    const { data, error } = await supabase
      .from('bannierepublicitaire')
      .select(VENDOR_BANNER_COLUMNS)
      .eq('idcommerce', marketplaceId)
      .eq('idvendeur', vendorId)
      .order('id', { ascending: false });
    if (error || !data) return [];
    return (data as Record<string, unknown>[]).map(mapVendorBanner);
  } catch {
    return [];
  }
}

export interface VendorBannerInput {
  marketplaceId: number;
  vendorId: number;
  titre: string;
  description: string;
  image: string;
  idannonce: number;
}

export async function createVendorBanner(input: VendorBannerInput): Promise<void> {
  const { error } = await supabase.from('bannierepublicitaire').insert({
    numerobanniere: 0,
    titre: input.titre,
    descriptioncourte: input.description,
    imagecouverture: input.image,
    idcommerce: input.marketplaceId,
    idvendeur: input.vendorId,
    idannonce: input.idannonce,
    click: 0,
  });
  if (error) throw error;
}

export async function updateVendorBanner(
  id: number,
  patch: { titre?: string; description?: string; image?: string; idannonce?: number },
): Promise<void> {
  const row: Record<string, unknown> = {};
  if (patch.titre !== undefined) row.titre = patch.titre;
  if (patch.description !== undefined) row.descriptioncourte = patch.description;
  if (patch.image !== undefined) row.imagecouverture = patch.image;
  if (patch.idannonce !== undefined) row.idannonce = patch.idannonce;
  const { error } = await supabase.from('bannierepublicitaire').update(row).eq('id', id);
  if (error) throw error;
}

export async function deleteVendorBanner(id: number): Promise<void> {
  const { error } = await supabase.from('bannierepublicitaire').delete().eq('id', id);
  if (error) throw error;
}

/** Incrémente le compteur de clics d'une bannière (best-effort). */
export async function incrementBannerClick(id: number, current: number): Promise<void> {
  try {
    await supabase.from('bannierepublicitaire').update({ click: current + 1 }).eq('id', id);
  } catch {
    /* best-effort */
  }
}

// ── Médias additionnels (galerie + vidéos) des contenus ────────

/** Découpe une liste de médias stockée en base (séparateur « | »). */
export function parseMediaList(value?: string | null): string[] {
  return (value || '')
    .split('|')
    .map((v) => v.trim())
    .filter(Boolean);
}

/** Remplace la galerie photos additionnelle d'une annonce (table annonces_images). */
export async function saveAnnonceGallery(annonceId: number, urls: string[]): Promise<void> {
  if (!Number.isFinite(annonceId) || annonceId <= 0) return;
  const clean = urls.map((u) => u.trim()).filter(Boolean);
  await supabase.from('annonces_images').delete().eq('idannonce', annonceId);
  if (clean.length === 0) return;
  const { error } = await supabase
    .from('annonces_images')
    .insert(clean.map((image) => ({ idannonce: annonceId, image })));
  if (error) throw error;
}

// ── Profil public de la boutique (logo + infos) ────────────────

export interface VendorShopProfilePatch {
  image?: string;
  nomcommerce?: string;
  description?: string;
  telephone?: string;
  horaires?: string;
  horaires_json?: OpeningHours | null;
  lienfacebook?: string;
  lieninstagram?: string;
  lientiktok?: string;
  lientwitter?: string;
  lienlinkedin?: string;
  lienyoutube?: string;
}

/** Met à jour le profil public de la boutique d'un vendeur (logo, infos, horaires, réseaux). */
export async function updateVendorShopProfile(
  userId: number,
  patch: VendorShopProfilePatch,
): Promise<void> {
  const row: Record<string, unknown> = {};
  if (patch.image !== undefined) row.image = patch.image;
  if (patch.nomcommerce !== undefined) row.nomcommerce = patch.nomcommerce;
  if (patch.description !== undefined) row.description = patch.description;
  if (patch.telephone !== undefined) row.telephone = patch.telephone;
  if (patch.horaires !== undefined) row.horaires = patch.horaires;
  if (patch.horaires_json !== undefined) row.horaires_json = patch.horaires_json;
  if (patch.lienfacebook !== undefined) row.lienfacebook = patch.lienfacebook;
  if (patch.lieninstagram !== undefined) row.lieninstagram = patch.lieninstagram;
  if (patch.lientiktok !== undefined) row.lientiktok = patch.lientiktok;
  if (patch.lientwitter !== undefined) row.lientwitter = patch.lientwitter;
  if (patch.lienlinkedin !== undefined) row.lienlinkedin = patch.lienlinkedin;
  if (patch.lienyoutube !== undefined) row.lienyoutube = patch.lienyoutube;
  if (Object.keys(row).length === 0) return;
  const { error } = await supabase.from('users').update(row).eq('id', userId);
  if (error) throw error;
}